import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Theme } from "@emotion/react";
import { Body2 } from "@iventis/styles/src/components/texts";
import { findNode, useTreeBrowser } from "@iventis/tree-browser";
import React from "react";
import { useSelector } from "react-redux";
import { styled } from "@iventis/styles";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { ViewByStatus } from "@iventis/map-engine/src/types/layer-view-by";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { useDataFieldServices } from "@iventis/datafield-editor";
import { setViewByDataField, setViewByObject } from "../sidebar.helpers";
import { dispatch, RootState } from "../state/root.store";
import { sidebar } from "../state/sidebar.slice";
import { RowFormattedPopover } from "../popover-row-format";

export const ViewBySelectorComponent = () => {
    const { nodeId } = useSelector((s: RootState) => ({ nodeId: s.sidebarReducer.popover.nodeId }));
    const { mapLayer } = useSelector((s: RootState) => ({ mapLayer: s.mapReducer.mapModule.layers.value.find((lyr) => lyr.id === nodeId) as DomainLayer }));
    const [{ tree }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, nodeId, "tree");
    const { dataFieldListItemsService } = useDataFieldServices();

    const availableDataFields = mapLayer.dataFields?.filter((df) => df.type === DataFieldType.List);

    return (
        <RowFormattedPopover>
            <StyledSelector
                hasBorderBottom={availableDataFields.length > 0}
                onClick={() => {
                    setViewByObject(mapLayer.id, treeBrowserContext, tree);
                    dispatch(sidebar.actions.closePopover());
                }}
            >
                <div className="icon">
                    <FontAwesomeIcon icon={["far", "cube"]} />
                </div>
                <Body2>View By {ViewByStatus.OBJECT}</Body2>
                <div className="icon">{mapLayer.viewBy === ViewByStatus.OBJECT && <FontAwesomeIcon icon={["far", "check"]} />}</div>
            </StyledSelector>
            {availableDataFields?.length > 0 && (
                <>
                    <StyledSelectorNonInteractive id="view-by-data-field">
                        <div className="icon">
                            <FontAwesomeIcon size="sm" icon={["fal", "tag"]} />
                        </div>
                        <Body2>View By {ViewByStatus.DATA_FIELD}</Body2>
                    </StyledSelectorNonInteractive>
                    {availableDataFields.map((dataField) => (
                        <StyledSelector
                            key={dataField.id}
                            isDataFieldItem
                            aria-labelledby="view-by-data-field"
                            onClick={() => {
                                setViewByDataField(dataField, mapLayer, treeBrowserContext, tree, dataFieldListItemsService);
                                dispatch(sidebar.actions.closePopover());
                            }}
                        >
                            <div />
                            <Body2>{dataField.name}</Body2>
                            <div className="icon">
                                {mapLayer.viewBy === ViewByStatus.DATA_FIELD &&
                                    dataField.listValues.some(({ id }) => id === findNode(tree.childNodes, mapLayer.id).childNodes[0]?.id) && (
                                        <FontAwesomeIcon icon={["far", "check"]} />
                                    )}
                            </div>
                        </StyledSelector>
                    ))}
                </>
            )}
        </RowFormattedPopover>
    );
};

const StyledSelector = styled(InteractiveElement)<{ isDataFieldItem?: boolean; hasBorderBottom?: boolean }>`
    display: grid;
    grid-template-columns: 34px auto 34px;
    padding: 0 5px;
    min-width: 212px;
    text-align: start;
    align-items: center;
    border-bottom: ${({ theme, hasBorderBottom }: { theme: Theme; hasBorderBottom: boolean }) => (hasBorderBottom ? `${theme.typographyColors.subdued} 0.5px solid` : "none")};
    :hover {
        background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.light20};
    }
    .icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    span {
        padding-left: ${(props) => (props.isDataFieldItem ? "5px" : 0)};
    }
`;

const StyledSelectorNonInteractive = styled.div`
    display: grid;
    grid-template-columns: 34px auto 34px;
    padding: 0 5px;
    min-width: 212px;
    text-align: start;
    align-items: center;
    .icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
